import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add auth token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Redirect to login on expired/invalid token
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      // Don't redirect if this was the login request itself
      const isLoginRequest = error.config && error.config.url && error.config.url.includes('/auth/login');
      if (!isLoginRequest) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.reload();
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (username, password) => api.post('/auth/login', { username, password }),
  getCurrentUser: () => api.get('/auth/me'),
  changePassword: (currentPassword, newPassword) => 
    api.post('/auth/change-password', { currentPassword, newPassword })
};

// Menu API
export const menuAPI = {
  getMenuItems: (dayOfWeekId, timeSlotId) => {
    const params = {};
    if (dayOfWeekId) params.dayOfWeekId = dayOfWeekId;
    if (timeSlotId) params.timeSlotId = timeSlotId;
    return api.get('/menu/items', { params });
  },
  getTimeSlots: () => api.get('/menu/time-slots'),
  getDaysOfWeek: () => api.get('/menu/days')
};

// Standing Orders API
export const standingOrdersAPI = {
  get: (userId) => api.get('/standing-orders', { params: { userId } }),
  update: (data) => api.post('/standing-orders/update', data),
  bulkUpdate: (data) => api.post('/standing-orders/bulk-update', data)
};

// Orders API
export const ordersAPI = {
  submit: (data) => api.post('/orders/submit', data),
  getHistory: (userId) => api.get('/orders/history', { params: { userId } }),
  getAll: (date, timeSlotId) => api.get('/orders/all', { params: { date, timeSlotId } }),
  updateStatus: (orderId, status) => api.patch(`/orders/${orderId}/status`, { status }),
  cancel: (orderId) => api.post(`/orders/${orderId}/cancel`)
};

// Order Skips API
export const orderSkipsAPI = {
  get: (userId) => api.get('/order-skips', { params: { userId } }),
  create: (data) => api.post('/order-skips', data),
  remove: (skipId) => api.delete(`/order-skips/${skipId}`)
};

// Admin API
export const adminAPI = {
  getUsers: () => api.get('/admin/users'),
  createUser: (data) => api.post('/admin/users', data),
  updateUser: (userId, data) => api.patch(`/admin/users/${userId}`, data),
  resetPassword: (userId, newPassword) => 
    api.post(`/admin/users/${userId}/reset-password`, { newPassword }),
  createTimeSlot: (data) => api.post('/admin/time-slots', data),
  updateTimeSlot: (timeSlotId, data) => api.patch(`/admin/time-slots/${timeSlotId}`, data),
  getTimeSlots: () => api.get('/admin/time-slots'),
  
  // Granular item lock management
  getAllItemLocks: () => api.get('/admin/item-locks'),
  getItemLocks: (itemId) => api.get(`/admin/item-locks/${itemId}`),
  addItemLock: (data) => api.post('/admin/item-locks', data),
  removeItemLock: (lockId) => api.delete(`/admin/item-locks/${lockId}`),
  
  // Menu item management
  getAllMenuItems: () => api.get('/admin/menu-items'),
  createMenuItem: (data) => api.post('/admin/menu-items', data),
  updateMenuItem: (itemId, data) => api.patch(`/admin/menu-items/${itemId}`, data),
  deleteMenuItem: (itemId) => api.delete(`/admin/menu-items/${itemId}`)
};

// Version API
export const versionAPI = {
  getBackendVersion: () => api.get('/version/backend'),
  getFrontendVersion: async () => {
    // Fetch version.json from public folder
    const response = await fetch('/version.json');
    if (!response.ok) {
      throw new Error('Failed to fetch frontend version');
    }
    return { data: await response.json() };
  }
};

// Audit API
export const auditAPI = {
  getLogs: (filters) => api.get('/audit', { params: filters }),
  getOrderLogs: (orderId) => api.get(`/audit/order/${orderId}`),
  getUserStandingOrderLogs: (userId) => api.get(`/audit/user/${userId}/standing-orders`)
};

// Reports API
export const reportsAPI = {
  getWeeklyStandingOrders: () => api.get('/reports/weekly-standing-orders'),
  getWeeklyStandingOrdersPrint: async () => {
    // Fetch HTML via authenticated API (token in header, not URL)
    const response = await api.get('/reports/weekly-standing-orders/print', {
      responseType: 'text'
    });
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(response.data);
      printWindow.document.close();
    }
  }
};

// Email Settings API
export const emailSettingsAPI = {
  getSettings: () => api.get('/email-settings'),
  updateSettings: (data) => api.put('/email-settings', data),
  testConnection: () => api.post('/email-settings/test-connection'),
  sendTestEmail: (email) => api.post('/email-settings/send-test', { email })
};

// Dashboard API
export const dashboardAPI = {
  getOrders: (date) => api.get(`/dashboard/orders?date=${date}`),
  getWeek: (start) => api.get(`/dashboard/week${start ? '?start=' + start : ''}`)
};

export default api;
